import { useState } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Search,
  Filter,
  ArrowUpDown,
  MoreHorizontal,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { Instructor } from "../../services/instructorService";
import { useAuth } from "../../contexts/AuthContext";

interface InstructorTableProps {
  instructors: Instructor[];
  isLoading?: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  statusFilter: string;
  sortField: string;
  sortOrder: "asc" | "desc";
  onSearchChange: (value: string) => void;
  onStatusChange: (status: string) => void;
  onSortChange: (field: string, order: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onAddClick?: () => void;
  onEditClick: (instructor: Instructor) => void;
  onDeleteClick: (instructor: Instructor) => void;
  onApproveClick: (instructor: Instructor) => void;
  onRejectClick: (instructor: Instructor) => void;
  extraHeaderActions?: React.ReactNode;
}

export function InstructorTable({
  instructors,
  isLoading,
  totalItems,
  currentPage,
  pageSize,
  searchTerm,
  statusFilter,
  sortField,
  sortOrder,
  onSearchChange,
  onStatusChange,
  onSortChange,
  onPageChange,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onApproveClick,
  onRejectClick,
  extraHeaderActions,
}: InstructorTableProps) {
  const { hasPermission } = useAuth();
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSortClick = (field: string) => {
    const newOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    onSortChange(field, newOrder);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#E8F5EE] text-[#1B8754]">
            <CheckCircle2 size={12} className="mr-1" />
            Approved
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FEECEC] text-[#FF4D49]">
            <XCircle size={12} className="mr-1" />
            Rejected
          </span>
        );
      case "pending":
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FFF8E6] text-[#FFB020]">
            <Clock size={12} className="mr-1" />
            Pending
          </span>
        );
    }
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Instructors
        </h2>
        <p className="text-gray-500">
          Manage your course instructors and their permissions.
        </p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full xl:w-[408px]">
          <Input
            placeholder="Search instructors"
            icon={<Search size={20} />}
            className="bg-white w-full"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter size={16} /> Filter
              </Button>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-3 min-w-[240px] w-full sm:w-max animate-in fade-in zoom-in duration-150">
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                      Status
                    </p>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          onStatusChange("all");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "all"
                            ? "bg-gray-100 font-bold"
                            : "text-gray-700"
                        }`}
                      >
                        All Status
                      </button>
                      <button
                        onClick={() => {
                          onStatusChange("approved");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "approved"
                            ? "bg-green-50 font-bold text-green-700"
                            : "text-gray-700"
                        }`}
                      >
                        Approved
                      </button>
                      <button
                        onClick={() => {
                          onStatusChange("pending");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "pending"
                            ? "bg-yellow-50 font-bold text-yellow-700"
                            : "text-gray-700"
                        }`}
                      >
                        Pending
                      </button>
                      <button
                        onClick={() => {
                          onStatusChange("rejected");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "rejected"
                            ? "bg-red-50 font-bold text-red-700"
                            : "text-gray-700"
                        }`}
                      >
                        Rejected
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsFilterOpen(false);
                }}
              >
                <ArrowUpDown size={16} /> Sort By
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[160px] animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        onSortChange("createdAt", "desc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "createdAt" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Date Joined
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("students", "desc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "students" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Students Count
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("courses", "desc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "courses" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Courses Count
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("experience", "desc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "experience"
                          ? "bg-gray-100 font-bold"
                          : ""
                      }`}
                    >
                      Years of Experience
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Extra header actions (e.g. Export CSV) + primary button */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {extraHeaderActions}
            {hasPermission('instructors:create') && (
              <Button
                variant="primary"
                size="lg"
                className="w-full sm:w-[208px] whitespace-nowrap h-[48px] sm:h-[44px] touch-target"
                onClick={onAddClick}
              >
                Add new instructor
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card className="p-0 border shadow-sm w-full overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[20%]">
                  Instructor Info
                </th>
                <th
                  className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSortClick("experience")}
                >
                  Expertise{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "experience"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[10%]">
                  Stats
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%]">
                  Status
                </th>
                <th
                  className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSortClick("createdAt")}
                >
                  Joined Date{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "createdAt"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th className="px-3 sm:px-6 py-4 w-[5%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    Loading instructors...
                  </td>
                </tr>
              ) : instructors.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    No instructors found
                  </td>
                </tr>
              ) : (
                instructors.map((instructor) => (
                  <tr
                    key={instructor._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center gap-3">
                        {instructor.user?.profilePicture ? (
                          <img
                            src={instructor.user.profilePicture}
                            alt={instructor.user.firstName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                            {instructor.user?.firstName?.[0]}
                            {instructor.user?.lastName?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#102040]">
                            {instructor.user?.firstName}{" "}
                            {instructor.user?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {instructor.professionalTitle}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">
                          {instructor.yearsOfExperience} years
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-1">
                          {instructor.expertiseAreas.join(", ")}
                        </p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm">
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-900 font-medium">
                          {instructor.statistics?.totalCourses || 0} courses
                        </span>
                        <span className="text-gray-500 text-xs">
                          {instructor.statistics?.totalStudents || 0} students
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      {getStatusBadge(instructor.approvalStatus)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {formatDate(instructor.createdAt)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right relative">
                      <button
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenu?.id === instructor._id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: instructor._id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>

                      <TableActionMenu
                        isOpen={activeMenu?.id === instructor._id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        {instructor.approvalStatus === "pending" && hasPermission('instructors:approve') && (
                          <>
                            <button
                              onClick={() => {
                                onApproveClick(instructor);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
                            >
                              <CheckCircle2 size={14} />
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                onRejectClick(instructor);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <XCircle size={14} />
                              Reject
                            </button>
                            <div className="h-px bg-gray-100 my-1" />
                          </>
                        )}
                        {hasPermission('instructors:update') && (
                          <button
                            onClick={() => {
                              onEditClick(instructor);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Edit2 size={14} className="text-blue-500" />
                            Edit Instructor
                          </button>
                        )}
                        {hasPermission('instructors:delete') && (
                          <button
                            onClick={() => {
                              onDeleteClick(instructor);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 size={14} />
                            Delete Instructor
                          </button>
                        )}
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Server-side Pagination UI */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            {totalItems > 0 ? (
              <>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)}{" "}
                to {Math.min(currentPage * pageSize, totalItems)} of{" "}
                {totalItems} instructors
              </>
            ) : (
              "No instructors found"
            )}
          </span>
          <div className="flex gap-1 ml-4">
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              &lt; Previous
            </button>
            <button className="px-3 py-1 bg-gray-900 text-white rounded">
              {currentPage}
            </button>
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === totalPages || totalItems === 0}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next &gt;
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
